import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import LoadingSpinner from '../components/LoadingSpinner';
import Paginate from '../components/Paginate';
import DateRangePicker from '../components/DateRangePicker';
import { Search } from 'lucide-react';
import Moment from 'react-moment';
import { Link } from 'react-router-dom';
import { debounce } from 'lodash';

const EmiList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [emiData, setEmiData] = useState([]);
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);

  // Initialize filters from URL params
  const [filters, setFilters] = useState({
    page: parseInt(searchParams.get('page')) || 1,
    limit: parseInt(searchParams.get('limit')) || 10,
    search: searchParams.get('search') || '',
    emiStatus: searchParams.get('emiStatus') || 'pending',
    startDate: searchParams.get('startDate') || null,
    endDate: searchParams.get('endDate') || null,
  });

  // Local search state for debouncing
  const [localSearch, setLocalSearch] = useState(filters.search);

  // Update URL params when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) {
        params.set(key, value);
      }
    });
    setSearchParams(params);
  }, [filters, setSearchParams]);

  // Debounced search handler
  const debouncedSearch = useCallback(
    debounce((value) => {
      setFilters(prev => ({ ...prev, search: value, page: 1 }));
    }, 500),
    []
  );

  const handleSearchChange = useCallback(
    (e) => {
      const value = e.target.value;
      setLocalSearch(value);
      debouncedSearch(value);
    },
    [debouncedSearch]
  );

  // Fetch EMI data with filters
  const fetchEmiData = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/loan/loans/emi/emi-list', {
        params: {
          ...filters,
          startDate: filters.startDate ? new Date(filters.startDate).toISOString() : undefined,
          endDate: filters.endDate ? new Date(filters.endDate).toISOString() : undefined,
        },
      });
      setEmiData(response.data.data.loanEmis);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error fetching EMI data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmiData();
  }, [filters]);

  const handleDateChange = (dates) => {
    setFilters(prev => ({
      ...prev,
      startDate: dates.startDate || null,
      endDate: dates.endDate || null,
      page: 1
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value,
      page: 1
    }));
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold">EMI Listing</h1>
      </div>
      
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search EMIs..."
            value={localSearch}
            onChange={handleSearchChange}
            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        
        <select
          name="emiStatus"
          value={filters.emiStatus}
          onChange={handleFilterChange}
          className="border rounded-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All Statuses</option>
          <option value="PAID">Paid</option>
          <option value="PENDING">Pending</option>
          <option value="OVERDUE">Overdue</option>
        </select>
        
        <div className="w-full sm:w-[300px]">
          <DateRangePicker
            startDate={filters.startDate}
            endDate={filters.endDate}
            onChange={handleDateChange}
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                <th className="border px-4 py-2 text-left">Loan EMI ID</th>
                <th className="border px-4 py-2 text-left">Loan ID</th>
                <th className="border px-4 py-2 text-left">EMI Amount</th>
                <th className="border px-4 py-2 text-left">EMI Status</th>
                <th className="border px-4 py-2 text-left">Due Date</th>
                <th className="border px-4 py-2 text-left">Paid Date</th>
                <th className="border px-4 py-2 text-left">Payment Status</th>
                <th className="border px-4 py-2 text-left">Payment Method</th>
                <th className="border px-4 py-2 text-left">Name</th>
                <th className="border px-4 py-2 text-left">Mobile Number</th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {emiData.map((emi) => (
                <tr key={emi.loanEmiId} className="hover:bg-gray-50">
                  <td className="border px-4 py-2">{emi.loanEmiId}</td>
                  <td className="border px-4 py-2">
                    <Link className="text-blue-600 hover:text-blue-800 underline" to={`/loans/${emi.loanAppId}`}>
                      {emi.loanAppId}
                    </Link>
                  </td>
                  <td className="border px-4 py-2">{emi.emiAmount}</td>
                  <td className="border px-4 py-2">{emi.emiStatus}</td>
                  <td className="border px-4 py-2">
                    <Moment tz="Asia/Kolkata" format="DD MMM YYYY hh:mm A">
                      {emi.emiDueDate}
                    </Moment>
                  </td>
                  <td className="border px-4 py-2">
                    {emi.paidDate? (
                      <Moment tz="Asia/Kolkata" format="DD MMM YYYY hh:mm A">
                        {emi.paidDate}
                      </Moment>
                    ):"-"}
                  </td>
                  <td className="border px-4 py-2">{emi.paymentStatus || "-"}</td>
                  <td className="border px-4 py-2">{emi.paymentMethod}</td>
                  <td className="border px-4 py-2">
                    <Link className="text-blue-600 hover:text-blue-800 underline" to={`/customers/view/${emi.id}`}>
                      {emi.name}
                    </Link>
                  </td>
                  <td className="border px-4 py-2">{emi.mobileNumber}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="pt-4">
        <Paginate
          currentPage={filters.page}
          totalItems={pagination.totalItems}
          pageSize={filters.limit}
          onPageChange={(page) => setFilters(prev => ({ ...prev, page }))}
          onPageSizeChange={(limit) => setFilters(prev => ({ ...prev, limit, page: 1 }))}
        />
      </div>
    </div>
  );
};

export default EmiList;